import * as React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Container,
  Stack,
  Link,
} from "@mui/material";
import { useState } from "react";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import RocketIcon from "@mui/icons-material/Rocket";
import IconButton from "@mui/material/IconButton";
import { useLocation } from "react-router-dom";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("entry.1045781291", email); // Replace XXXX with your field ID

      await fetch(
        "https://docs.google.com/forms/u/0/d/e/1FAIpQLSdyF2h66VcrVfCfhZH0rzCVo7GHZM7wfw1Xo8HXJyFb3NIsZg/formResponse",
        {
          method: "POST",
          body: formData,
          mode: "no-cors", // Important for CORS issues
        }
      );

      setShowModal(true);
      setEmail("");
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: "auto",
          py: 4,
          px: 2,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            color="text.primary"
          >
            ¡Mantente Informado!
          </Typography>
          <Typography sx={{ color: "text.secondary", mt: 1 }}>
            Suscríbete a nuestro newsletter para informarte sobre las últimas
            noticias aeroespaciales
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <TextField
            fullWidth
            type="email"
            label="Ingresa tu correo electrónico"
            variant="filled"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 48,
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              height: 48,
              fontWeight: "bold",
              border: 2,
              borderColor: "primary.light",
              background:
                "linear-gradient(90deg,rgba(0, 79, 140, 1) 0%, rgba(0, 48, 89, 1) 100%);",

              color: "white",
              "&:hover": {
                background:
                  "linear-gradient(90deg,rgba(0, 126, 222, 1) 0%, rgba(0, 95, 168, 1) 100%);",
              },
              position: "relative",
              overflow: "hidden",
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "¡Suscribirme!"
            )}
          </Button>
        </Box>
      </Box>

      {/* Success Dialog */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent
          sx={{
            textAlign: "center",
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            borderRadius: 2,
            border: 2,
            borderColor: "primary.light",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              bgcolor: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RocketIcon color="primary" sx={{ fontSize: 40 }} />
          </Box>

          <DialogTitle sx={{ fontWeight: "bold", px: 0 }}>
            Suscripción Exitosa
          </DialogTitle>

          <DialogContentText color="text.secondary">
            Gracias por suscribirte a nuestro newsletter. Recibirás las últimas
            actualizaciones y contenido exclusivo directamente en tu bandeja de
            entrada.
          </DialogContentText>

          <DialogActions sx={{ justifyContent: "center", px: 0 }}>
            <Button
              onClick={() => setShowModal(false)}
              variant="contained"
              sx={{
                background:
                  "linear-gradient(90deg,rgba(0, 79, 140, 1) 0%, rgba(0, 48, 89, 1) 100%)",
                color: "white",
                "&:hover": {
                  background:
                    "linear-gradient(90deg,rgba(0, 126, 222, 1) 0%, rgba(0, 95, 168, 1) 100%)",
                },
              }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function ContactAcademia() {
  const location = useLocation();
  const isSpecificPage = location.pathname === "/academia";
  return (
    <Box
      id="contactAcademia"
      sx={(theme) => ({
        width: "100%",
        backgroundRepeat: "no-repeat",
        backgroundImage: isSpecificPage
          ? "radial-gradient(ellipse 80% 50% at 50% 50%, hsl(210, 100%, 90%), transparent)" // Specific page color
          : "radial-gradient(ellipse 80% 50% at 50% 50%, hsl(30, 100%, 80%), transparent)",
        ...theme.applyStyles("dark", {
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 50% 50%, hsl(210, 100%, 16%), transparent)",
        }),
      })}
    >
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <Stack
          spacing={2}
          useFlexGap
          sx={{ alignItems: "center", width: { xs: "100%", sm: "70%" } }}
        >
          <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: "clamp(3rem, 10vw, 3.5rem)",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "center", sm: "baseline" },
                lineHeight: 1.2,
              }}
            >
              Contacta
              <Typography
                component="span"
                variant="h1"
                sx={(theme) => ({
                  fontSize: "inherit",
                  color: "primary.main",
                  display: "inline-block",
                  ml: { sm: 1 }, // small margin left only on desktop
                  ...theme.applyStyles("dark", {
                    color: "primary.light",
                  }),
                })}
              >
                a nuestro equipo
              </Typography>
            </Typography>
          </Box>

          <Typography
            component="h2"
            variant="h5"
            sx={{ color: "text.secondary", mt: 2, textAlign: "center" }}
          >
            ¡Escríbenos un mensaje para pedir información, solicitar tu curso o
            taller, o colaborar con nosotros como tallerista o instructor/a!
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              p: { xs: 2, sm: 3 },
              backgroundColor: "#0e5d86",
              borderRadius: 2,
              height: { xs: "auto", sm: 60 }, // Auto height on mobile, fixed on desktop
              width: { xs: "100%", sm: 600 }, // Full width on mobile, fixed on desktop
              boxSizing: "border-box",
              gap: { xs: 2, sm: 0 },
            }}
          >
            {/* Text on left - fixed width */}
            <Typography
              component="h2"
              variant="h5"
              sx={{
                color: "common.white",
                textAlign: { xs: "center", sm: "left" },
                minWidth: 200, // Fixed minimum width for text
                px: 2,
                whiteSpace: "nowrap", // Prevent text wrapping
              }}
            >
              academia@enmice.mx
            </Typography>

            {/* Button on right - fixed width */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              onClick={() =>
                window.open(
                  "https://docs.google.com/forms/u/0/d/e/1FAIpQLSdyF2h66VcrVfCfhZH0rzCVo7GHZM7wfw1Xo8HXJyFb3NIsZg/formResponse",
                  "_blank"
                )
              }
              sx={{
                width: { xs: "100%", sm: 200 }, // Full width on mobile, fixed on desktop
                height: 48,
                fontWeight: "bold",
                border: 2,
                borderColor: "primary.light",
                background:
                  "linear-gradient(90deg, rgba(0, 79, 140, 1) 0%, rgba(0, 48, 89, 1) 100%)",
                color: "white",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, rgba(0, 126, 222, 1) 0%, rgba(0, 95, 168, 1) 100%)",
                },
                position: "relative",
                overflow: "hidden",
                flexShrink: 0, // Prevent button from shrinking
              }}
            >
              Suscribirme
            </Button>
          </Box>

          <Typography
            component="h2"
            variant="h5"
            sx={{ color: "text.secondary", mt: 2, textAlign: { xs: "center" } }}
          >
            ¡O búscanos en nuestras redes sociales!
          </Typography>
          <Stack
            direction="row"
            spacing={6}
            useFlexGap
            sx={{ justifyContent: "left", color: "text.secondary", mt: 1 }}
          >
            <IconButton
              sx={{
                alignSelf: "center",
                color: "inherit",
              }}
              href="https://www.facebook.com/enmicemx"
              aria-label="Facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FacebookIcon sx={{ fontSize: 60 }} />
            </IconButton>
            <IconButton
              sx={{
                alignSelf: "center",
                color: "inherit",
              }}
              href="https://www.instagram.com/enmicemx"
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
              <InstagramIcon sx={{ fontSize: 60 }} />
            </IconButton>
            <IconButton
              sx={{
                alignSelf: "center",
                color: "inherit",
              }}
              href="https://mx.linkedin.com/company/enmicemx"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedInIcon sx={{ fontSize: 60 }} />
            </IconButton>
            <IconButton
              sx={{
                alignSelf: "center",
                color: "inherit",
              }}
              href="https://www.youtube.com/@ENMICE"
              aria-label="YouTube"
              target="_blank"
              rel="noopener noreferrer"
            >
              <YouTubeIcon sx={{ fontSize: 60 }} />
            </IconButton>
          </Stack>

          <Typography
            component="h2"
            variant="h4"
            sx={{ color: "text.primary", mt: 1 }}
          >
            <Link
              href="mailto:contacto@enmice.mx"
              target="_blank"
              variant="h5"
              sx={{
                fontWeight: "bold",
                display: "inline-flex",
                alignItems: "center",
                "& > svg": { transition: "0.2s" },
                "&:hover > svg": { transform: "translateX(2px)" },
              }}
            >
              <span>contacto@enmice.mx</span>
              <ChevronRightRoundedIcon
                fontSize="small"
                sx={{ mt: "1px", ml: "2px" }}
              />
            </Link>
          </Typography>

          {/* Newsletter Subscription Form */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 400,
              mt: 4,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <NewsletterForm />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
